import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { listUsers, updateUser, deactivateUser, reactivateUser, inviteUser, uploadAvatar } from "@/integrations/backend/admin-users";
import { CATALOG_STALE_BASE_MS, staleTimeWithJitterFromKey } from "@/lib/query-cache";
import { queryKeys } from "@/lib/query-keys";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Search, Loader2, Edit3, Ban, RotateCcw, Upload } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogScrollContent,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type UserData = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  avatar_url: string | null;
  role: string | null;
  status: string | null;
  last_sign_in_at: string | null;
  created_at: string | null;
  bio: string | null;
  onboarding_completed: boolean | null;
  last_payment_at: string | null;
};

const usersQueryKey = queryKeys.admin.users;

export function UsersView() {
  const queryClient = useQueryClient();
  const usersQuery = useQuery({
    queryKey: usersQueryKey,
    queryFn: listUsers,
    staleTime: staleTimeWithJitterFromKey(CATALOG_STALE_BASE_MS, [...usersQueryKey]),
  });
  const users = (usersQuery.data ?? []) as UserData[];

  useEffect(() => {
    if (usersQuery.isError && usersQuery.error) {
      toast({
        title: "Error loading users",
        description: (usersQuery.error as Error)?.message ?? String(usersQuery.error),
        variant: "destructive",
      });
    }
  }, [usersQuery.isError, usersQuery.error]);

  const [userSearch, setUserSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all"); // account status only
  const [onboardingFilter, setOnboardingFilter] = useState<string>("all");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editRole, setEditRole] = useState<string>("client");
  const [editBio, setEditBio] = useState<string>("");
  const [editAvatarUrl, setEditAvatarUrl] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [deactivateUserId, setDeactivateUserId] = useState<string | null>(null);
  const [reactivateUserId, setReactivateUserId] = useState<string | null>(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<string>("admin");
  const [inviting, setInviting] = useState(false);

  const openEditDialog = (user: UserData) => {
    setSelectedUser(user);
    setEditFirstName(user.first_name || "");
    setEditLastName(user.last_name || "");
    setEditEmail(user.email || "");
    setEditRole(user.role || "client");
    setEditBio(user.bio || "");
    setEditAvatarUrl(user.avatar_url || null);
    setAvatarFile(null);
    setPreviewUrl(null);
    setEditDialogOpen(true);
  };

  const handleUploadAvatar = async (): Promise<string | null> => {
    if (!avatarFile) return editAvatarUrl;
    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!validTypes.includes(avatarFile.type)) {
      toast({ title: "Invalid file type", description: "Upload JPG, PNG, or WEBP", variant: "destructive" });
      return editAvatarUrl;
    }
    if (avatarFile.size > 5 * 1024 * 1024) {
      toast({ title: "File too large", description: "Max 5MB", variant: "destructive" });
      return editAvatarUrl;
    }
    try {
      const url = await uploadAvatar(avatarFile);
      return url;
    } catch (error: any) {
      toast({ title: "Upload failed", description: error?.message ?? String(error), variant: "destructive" });
      return editAvatarUrl;
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedUser) return;

    try {
      const newAvatarUrl = await handleUploadAvatar();
      await updateUser(selectedUser.id, {
        first_name: editFirstName.trim() || null,
        last_name: editLastName.trim() || null,
        bio: editBio.trim() || null,
        avatar_url: newAvatarUrl,
        role: editRole as "admin" | "trainer" | "client",
      });

      toast({
        title: "User updated successfully",
        description: "Profile changes have been saved.",
      });

      setEditDialogOpen(false);
      await queryClient.invalidateQueries({ queryKey: usersQueryKey });
    } catch (error: any) {
      console.error("Error updating user:", error);
      toast({
        title: "Error updating user",
        description: error?.message ?? String(error),
        variant: "destructive",
      });
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateUserId) return;

    try {
      await deactivateUser(deactivateUserId);

      toast({
        title: "User deactivated",
        description: "The user has been deactivated and cannot access the app.",
      });

      setDeactivateUserId(null);
      await queryClient.invalidateQueries({ queryKey: usersQueryKey });
    } catch (error: any) {
      console.error("Error deactivating user:", error);
      toast({
        title: "Error deactivating user",
        description: error?.message ?? String(error),
        variant: "destructive",
      });
    }
  };

  const handleReactivate = async () => {
    if (!reactivateUserId) return;

    try {
      await reactivateUser(reactivateUserId);

      toast({
        title: "User reactivated",
        description: "The user can now access the app again.",
      });

      setReactivateUserId(null);
      await queryClient.invalidateQueries({ queryKey: usersQueryKey });
    } catch (error: any) {
      console.error("Error reactivating user:", error);
      toast({
        title: "Error reactivating user",
        description: error?.message ?? String(error),
        variant: "destructive",
      });
    }
  };

  const handleInvite = async () => {
    const email = inviteEmail.trim();
    if (!email) {
      toast({ title: "Email required", description: "Enter an email to invite.", variant: "destructive" });
      return;
    }
    try {
      setInviting(true);
      await inviteUser(email, inviteRole as "admin" | "trainer" | "client");

      toast({
        title: "Invitation sent",
        description: "We emailed an invite so the user can set a password.",
      });
      setInviteDialogOpen(false);
      setInviteEmail("");
      setInviteRole("admin");
      await queryClient.invalidateQueries({ queryKey: usersQueryKey });
    } catch (err: any) {
      console.error("Error sending invite:", err);
      toast({ title: "Invite failed", description: err?.message ?? String(err), variant: "destructive" });
    } finally {
      setInviting(false);
    }
  };

  const filteredUsers = users.filter((user) => {
    const searchTerm = userSearch.toLowerCase();
    const matchesSearch =
      user.first_name?.toLowerCase().includes(searchTerm) ||
      user.last_name?.toLowerCase().includes(searchTerm) ||
      user.email?.toLowerCase().includes(searchTerm);

    const matchesRole = roleFilter === "all" || user.role === roleFilter;
    const matchesStatus = statusFilter === "all" || user.status === statusFilter;
    const matchesOnboarding =
      onboardingFilter === "all" ||
      user.role !== "client" ||
      (onboardingFilter === "completed" && user.onboarding_completed) ||
      (onboardingFilter === "not_completed" && !user.onboarding_completed);

    return matchesSearch && matchesRole && matchesStatus && matchesOnboarding;
  });

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getInitials = (firstName: string | null, lastName: string | null) => {
    const first = firstName?.[0] || "";
    const last = lastName?.[0] || "";
    return (first + last).toUpperCase() || "?";
  };

  const getRoleBadgeColor = (role: string | null) => {
    switch (role) {
      case "admin":
        return "bg-red-100 text-red-800 border-red-200";
      case "trainer":
        return "bg-blue-100 text-blue-800 border-blue-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getAccountStatusBadge = (status: string | null) => {
    if (status === "deactivated") return { label: "Deactivated", classes: "bg-red-100 text-red-800 border-red-200" } as const;
    return { label: "Active", classes: "bg-green-100 text-green-800 border-green-200" } as const;
  };

  if (usersQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Users</h1>
          <p className="text-gray-600">
            Manage user accounts and permissions ({filteredUsers.length} users)
          </p>
        </div>
        <div>
          <Button onClick={() => setInviteDialogOpen(true)}>
            Invite User
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="relative md:col-span-2">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search users by name or email..."
            className="pl-10"
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
          />
        </div>
        <div>
          <Label className="sr-only">Role Filter</Label>
          <select
            className="w-full border rounded-md h-10 px-3 text-sm"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="all">All Roles</option>
            <option value="admin">Admin</option>
            <option value="trainer">Trainer</option>
            <option value="client">Client</option>
          </select>
        </div>
        <div>
          <Label className="sr-only">Status Filter</Label>
          <select
            className="w-full border rounded-md h-10 px-3 text-sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="deactivated">Deactivated</option>
          </select>
        </div>
        <div>
          <Label className="sr-only">Onboarding Filter</Label>
          <select
            className="w-full border rounded-md h-10 px-3 text-sm"
            value={onboardingFilter}
            onChange={(e) => setOnboardingFilter(e.target.value)}
          >
            <option value="all">All Onboarding</option>
            <option value="completed">Completed</option>
            <option value="not_completed">Not completed</option>
          </select>
        </div>
      </div>

      {filteredUsers.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-gray-500">No users found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredUsers.map((user) => (
            <Card key={user.id} className="hover:shadow-xl transition-shadow">
              <CardContent className="p-5">
                <div className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <Avatar className="w-12 h-12">
                          {user.avatar_url ? (
                            <AvatarImage src={user.avatar_url} alt={`${user.first_name} ${user.last_name}`} />
                          ) : (
                            <AvatarFallback className="bg-primary text-black text-sm font-semibold">
                              {getInitials(user.first_name, user.last_name)}
                            </AvatarFallback>
                          )}
                        </Avatar>
                        <div>
                          <p className="font-semibold text-gray-800 leading-tight">
                            {user.first_name || user.last_name
                              ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
                              : "Unnamed User"}
                          </p>
                          <p className="text-sm text-gray-600 leading-tight truncate max-w-[150px]">
                            {user.email || "No email"}
                          </p>
                        </div>
                      </div>
                      <Badge variant="outline" className={`text-xs capitalize ${getRoleBadgeColor(user.role)}`}>
                        {user.role || "client"}
                      </Badge>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {(() => { const a = getAccountStatusBadge(user.status); return <Badge variant="outline" className={`text-xs ${a.classes}`}>{a.label}</Badge>; })()}
                      {user.role === "client" && (
                        <Badge variant="outline" className={`text-xs ${user.onboarding_completed ? "bg-blue-100 text-blue-800 border-blue-200" : "bg-orange-100 text-orange-800 border-orange-200"}`}>
                          Onboarding: {user.onboarding_completed ? "Completed" : "Not completed"}
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="pt-3 border-t space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">Last login:</span>
                      <span className="text-gray-700 font-medium">
                        {formatDate(user.last_sign_in_at)}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">Last paid:</span>
                      <span className="text-gray-700 font-medium">
                        {formatDate(user.last_payment_at)}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">Joined:</span>
                      <span className="text-gray-700 font-medium">
                        {formatDate(user.created_at)}
                      </span>
                    </div>
                  </div>

                  <div className="pt-3 flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => openEditDialog(user)}
                    >
                      <Edit3 className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                    {user.status === "deactivated" ? (
                      <Button
                        size="sm"
                        className="flex-1 bg-green-600 text-white hover:bg-green-700"
                        onClick={() => setReactivateUserId(user.id)}
                      >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Reactivate
                      </Button>
                    ) : (
                      <Button
                        variant="destructive"
                        size="sm"
                        className="flex-1"
                        onClick={() => setDeactivateUserId(user.id)}
                      >
                        <Ban className="w-4 h-4 mr-2" />
                        Deactivate
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogScrollContent className="bg-white">
          <DialogHeader className="shrink-0">
            <DialogTitle className="text-gray-900">Edit User Profile</DialogTitle>
            <DialogDescription className="text-gray-700">
              Update the user's profile information.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4 py-2">
            {/* Avatar uploader */}
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16 border">
                <AvatarImage src={(previewUrl || editAvatarUrl) || undefined} />
                <AvatarFallback className="bg-primary text-black">
                  {getInitials(editFirstName, editLastName)}
                </AvatarFallback>
              </Avatar>
              <div>
                <Label htmlFor="edit-avatar" className="text-gray-900">Profile Image</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input id="edit-avatar" type="file" accept="image/jpeg,image/jpg,image/png,image/webp" onChange={(e)=>{ const f=e.target.files?.[0]; if(!f){setAvatarFile(null); setPreviewUrl(null);return;} setAvatarFile(f); setPreviewUrl(URL.createObjectURL(f)); }} />
                  { (editAvatarUrl || previewUrl) && (
                    <Button type="button" variant="outline" onClick={()=>{ setAvatarFile(null); setPreviewUrl(null); setEditAvatarUrl(null); }}>
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="first-name" className="text-gray-900">First Name</Label>
              <Input
                id="first-name"
                value={editFirstName}
                onChange={(e) => setEditFirstName(e.target.value)}
                placeholder="Enter first name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="last-name" className="text-gray-900">Last Name</Label>
              <Input
                id="last-name"
                value={editLastName}
                onChange={(e) => setEditLastName(e.target.value)}
                placeholder="Enter last name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-900">Email</Label>
              <Input
                id="email"
                type="email"
                value={editEmail}
                readOnly
                disabled
                placeholder="Enter email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bio" className="text-gray-900">Bio</Label>
              <Input id="bio" value={editBio} onChange={(e)=>setEditBio(e.target.value)} placeholder="Short bio" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role" className="text-gray-900">User Type</Label>
              <select id="role" className="w-full border rounded-md h-10 px-3 text-sm" value={editRole} onChange={(e)=>setEditRole(e.target.value)}>
                <option value="admin">Admin</option>
                <option value="trainer">Trainer</option>
                <option value="client">Client</option>
              </select>
            </div>
          </DialogBody>
          <DialogFooter className="shrink-0">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="text-black border-gray-300 hover:bg-gray-100">
              Cancel
            </Button>
            <Button onClick={handleSaveEdit}>Save Changes</Button>
          </DialogFooter>
        </DialogScrollContent>
      </Dialog>

      {/* Invite User Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogScrollContent className="bg-white">
          <DialogHeader className="shrink-0">
            <DialogTitle className="text-gray-900">Invite User</DialogTitle>
            <DialogDescription className="text-gray-700">
              Send an invite email. The user will set a password and complete sign-in.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="invite-email" className="text-gray-900">Email</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="name@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-role" className="text-gray-900">Role</Label>
              <select
                id="invite-role"
                className="w-full border rounded-md h-10 px-3 text-sm bg-gray-50 cursor-not-allowed"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                disabled
              >
                <option value="admin">Admin</option>
              </select>
            </div>
          </DialogBody>
          <DialogFooter className="shrink-0">
            <Button variant="outline" onClick={() => setInviteDialogOpen(false)} disabled={inviting} className="text-black border-gray-300 hover:bg-gray-100">
              Cancel
            </Button>
            <Button onClick={handleInvite} disabled={inviting}>
              {inviting ? <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Sending...</span> : "Send Invite"}
            </Button>
          </DialogFooter>
        </DialogScrollContent>
      </Dialog>

      {/* Deactivate Confirmation Dialog */}
      <AlertDialog open={Boolean(deactivateUserId)} onOpenChange={(open) => !open && setDeactivateUserId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate User?</AlertDialogTitle>
            <AlertDialogDescription>
              This will prevent the user from accessing the app. They will see a deactivation message if they try to log in.
              You can reactivate the user at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-black border border-gray-300 hover:bg-gray-100">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeactivate} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reactivate Confirmation Dialog */}
      <AlertDialog open={Boolean(reactivateUserId)} onOpenChange={(open) => !open && setReactivateUserId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Reactivate User?</AlertDialogTitle>
            <AlertDialogDescription>
              This will restore the user's access to the app.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-black border border-gray-300 hover:bg-gray-100">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleReactivate} className="bg-green-600 text-white hover:bg-green-700">
              Reactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
